import { Loader2 } from "lucide-react";
import { useState } from "react";
import { Navigate, useSearchParams } from "react-router";

import { AudioRippleBackground } from "@/components/audio-ripple-background";
import { JJWordmark } from "@/components/jj-wordmark";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAuthConfig, useLoginWithPassword, useMe } from "@/lib/api";
import { logger } from "@/lib/logger";

// Backend redirects to /login?error=<code> when SSO fails. Unknown codes fall
// back to the generic message below.
const SSO_ERROR_MESSAGES: Record<string, string> = {
  "domain-not-allowed":
    "This Microsoft account's email domain isn't allowed here. Sign in with your corporate account.",
  "missing-email": "Your Microsoft account has no email attached.",
};
const GENERIC_SSO_ERROR = "Sign-in failed, please try again — contact an admin if it persists.";

export function LoginPage() {
  const { data: me } = useMe();
  const authConfig = useAuthConfig();
  const [searchParams] = useSearchParams();

  // Already signed in (e.g. returning to /login with a live cookie) → skip the page.
  if (me) {
    return <Navigate to="/home" replace />;
  }

  const errorCode = searchParams.get("error");
  const errorMessage = errorCode ? (SSO_ERROR_MESSAGES[errorCode] ?? GENERIC_SSO_ERROR) : null;

  const handleSignIn = () => {
    // Full-page redirect (NOT an XHR): the backend bounces the browser to
    // Microsoft, then back to /home with the session cookie set.
    logger.debug("[auth] starting Microsoft SSO redirect");
    window.location.href = "/api/auth/sso/login";
  };

  return (
    <div className="bg-background relative flex min-h-screen flex-col items-center justify-center px-4 py-10">
      <AudioRippleBackground />
      <div className="relative z-10 mb-8 flex flex-col items-center gap-3 bg-[radial-gradient(ellipse_70%_65%_at_center,rgba(249,248,247,1)_0%,rgba(249,248,247,0.9)_45%,rgba(249,248,247,0)_80%)] px-16 py-2">
        <JJWordmark size="lg" />
        <span className="text-muted text-xs font-medium tracking-[0.18em] uppercase">
          Vision Proactive Communications
        </span>
      </div>

      <Card className="bg-card/85 relative z-10 w-full max-w-md backdrop-blur-md">
        <CardHeader className="space-y-2 pb-2">
          <CardTitle className="font-display text-foreground text-3xl font-normal tracking-tight">
            Sign in
          </CardTitle>
          <p className="text-muted text-sm leading-relaxed">
            Order-event communications, backorder outreach, and rep-reviewed order-share nudges in
            one secure workspace.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {errorMessage && (
            <div
              role="alert"
              className="border-destructive/30 bg-destructive/10 text-destructive rounded-md border px-3 py-2 text-sm"
            >
              {errorMessage}
            </div>
          )}
          {/* Render nothing provider-specific until the config answers — a
              Microsoft-button flash on a password deployment (or vice versa)
              is worse than a beat of spinner. */}
          {authConfig.isPending ? (
            <Loader2 className="text-wg-05 mx-auto my-4 size-5 animate-spin" aria-label="Loading" />
          ) : authConfig.isError ? (
            <div className="space-y-3">
              <p className="text-muted text-sm">
                Couldn&rsquo;t reach the sign-in service. Please try again.
              </p>
              <Button type="button" onClick={() => authConfig.refetch()} className="w-full">
                Retry
              </Button>
            </div>
          ) : authConfig.data?.provider === "supabase" ? (
            <PasswordLoginForm />
          ) : (
            <>
              <Button type="button" onClick={handleSignIn} className="w-full">
                <MicrosoftLogo />
                Sign in with Microsoft
              </Button>
              <p className="text-muted text-xs leading-relaxed">
                You&rsquo;ll be redirected to Microsoft to sign in with your Johnson &amp; Johnson
                account.
              </p>
            </>
          )}
        </CardContent>
      </Card>

      <a
        href="https://www.boardofinnovation.com"
        target="_blank"
        rel="noopener noreferrer"
        className="text-muted hover:text-foreground relative z-10 mt-8 text-xs tracking-wide transition-colors"
      >
        A Board of Innovation demo
      </a>
    </div>
  );
}

// Email + password form, rendered when the backend runs the Supabase auth
// provider. Validation is the server's job — we only gate the empty form.
function PasswordLoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const login = useLoginWithPassword();

  const errorMessage = login.error
    ? login.error.status === 401
      ? "Incorrect email or password."
      : login.error.status === 403
        ? "This email's domain isn't allowed here. Use your corporate account."
        : GENERIC_SSO_ERROR
    : null;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    logger.debug("[auth] submitting password login");
    // The successful response seeds the "me" query, so the redirect above
    // fires on the next render — no explicit navigate() needed.
    login.mutate({ email, password });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <label htmlFor="email" className="text-muted text-sm font-medium">
          Email
        </label>
        <Input
          id="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <label htmlFor="password" className="text-muted text-sm font-medium">
          Password
        </label>
        <Input
          id="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
      </div>
      {errorMessage && (
        <p role="alert" className="text-destructive text-sm">
          {errorMessage}
        </p>
      )}
      <Button type="submit" className="w-full" disabled={login.isPending || !email || !password}>
        {login.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
      </Button>
    </form>
  );
}

// Official Microsoft four-square mark — brand-fixed colors, not the app theme.
function MicrosoftLogo() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
